/**
 * LeadCard — v2 of the kanban card.
 *
 * Improvements over the original inline LeadCard:
 *   - status chip + signal dot in the header
 *   - "última actividad: hace Xd" indicator
 *   - meta row with próxima acción (relative date) + responsable avatar
 *   - quick actions revealed on hover/focus (Mover, Asignar, Próxima acción)
 *
 * Backend usage (no schema changes):
 *   - PATCH /leads/:id { status }                 → Mover
 *   - PATCH /leads/:id { assigned_to_user_id }    → Asignar
 *   - PATCH /leads/:id { next_action, next_action_date } → Próxima acción
 */
import React, { useState } from "react";
import {
  CaretRight, ArrowRight, ArrowLeft, DotsThreeVertical, UserCircle, Calendar, ChatCircleDots, Tag,
  X, Check, Handshake, EnvelopeSimple, Warning, ArrowsClockwise, Snowflake,
  WhatsappLogo,
} from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import Avatar from "@/components/ui-dv/Avatar";
import MetaRow from "@/components/ui-dv/MetaRow";
import {
  LEAD_STATUS_MAP, normalizeStatus, SIGNAL_META,
} from "@/lib/leadStatuses";
import {
  visibleStep, pipelineLabel, isColdLead,
  VISIBLE_PIPELINE_WITH_COLD, nextVisibleStatus, prevVisibleStatus,
  needsConfirmMove, confirmMoveText, COLD_LEAD_STATUS,
} from "@/lib/leadPipeline";
import { relativeActivity, relativeDueDate } from "@/lib/relativeDate";
import { awaitingReply, hasConversation } from "@/lib/conversationsSelectors";

const SIGNAL_TONE = { amber: "amber", red: "red", gray: "stone" };

function statusTone(key) {
  // Map dot color → chip tone (visually consistent without exposing raw hex).
  switch (key) {
    case "new":                return "slate";
    case "contacted":          return "blue";
    case "meeting_scheduled":  return "cyan";
    case "dossier":            return "violet";
    case "in_followup":        return "amber";
    case "reserved":           return "emerald";
    case "lost":               return "stone";
    default:                   return "stone";
  }
}

export default function LeadCard({
  lead,
  assignees = [],
  onOpen,
  onPatch,           // (lead, payload) => Promise
  selectable = false,
  selected = false,
  onToggleSelect,    // (lead) => void
}) {
  const [expandedAction, setExpandedAction] = useState(null); // null | "assign" | "next" | "stage"

  const statusKey = normalizeStatus(lead.status);
  const status = LEAD_STATUS_MAP[statusKey];
  const signals = lead.signals || [];
  const critical = signals.includes("overdue_action");
  const assignee = assignees.find(u => u.user_id === lead.assigned_to_user_id);

  const due = relativeDueDate(lead.next_action_date);
  const dueText = due
    ? (lead.next_action_time ? `${due.text} · ${lead.next_action_time}` : due.text)
    : null;
  const activity = relativeActivity(lead.updated_at || lead.created_at);

  // Compact context line — last note preferred, last logged message as fallback.
  // Always rendered as a single truncated line; omitted when no source exists.
  const contextLine = (() => {
    const notes = lead.notes_thread || [];
    const lastNote = notes.length ? notes[notes.length - 1] : null;
    if (lastNote && lastNote.text) {
      return {
        kind: "note",
        text: lastNote.text,
        when: relativeActivity(lastNote.at),
        who: lastNote.author_name,
      };
    }
    const msgs = lead.messages_log || [];
    const lastMsg = msgs.length ? msgs[msgs.length - 1] : null;
    if (lastMsg) {
      const text = lastMsg.summary || lastMsg.body || lastMsg.subject || lastMsg.kind;
      if (text) {
        return {
          kind: "msg",
          text: String(text),
          when: relativeActivity(lastMsg.at || lastMsg.sent_at || lastMsg.created_at),
          who: null,
        };
      }
    }
    return null;
  })();

  // Meeting chip — only render when meeting is "live" (scheduled). When done
  // or cancelled it stays only in the activity log to keep the card quiet.
  const meetingChip = (() => {
    if (lead.meeting_state !== "scheduled" || !lead.meeting_at) return null;
    const rel = relativeDueDate(lead.meeting_at);
    if (!rel) return null;
    const overdue = rel.tone === "red";
    return {
      icon: <Handshake size={11} weight="bold" />,
      text: overdue ? `Reunión vencida · ${rel.text}` : `Reunión ${rel.text}`,
      tone: overdue ? "red" : "cyan",
      title: lead.meeting_at,
    };
  })();

  // A3 — meeting was marked done but lead still on meeting_scheduled column.
  // Surface a soft CTA so the user advances the pipeline (or chooses not to).
  const meetingDoneSuggestion = (
    lead.meeting_state === "done" && statusKey === "meeting_scheduled"
      ? { icon: <ChatCircleDots size={11} weight="bold" />, text: "Reunión hecha · ¿Enviar dosier?", tone: "cyan" }
      : null
  );

  // Initial email auto-reply badge — discreet, fed by the public intake
  // (Codex/Resend reports the result; the platform does not send the email).
  const initialEmailChip = (() => {
    const ie = lead.initial_email;
    if (!ie) return null;
    const rel = relativeActivity(ie.sent_at);
    const isOk = ie.status === "ok";
    if (isOk) {
      const recent = (() => {
        if (!ie.sent_at) return false;
        try {
          const ms = Date.now() - new Date(ie.sent_at).getTime();
          return ms < 24 * 60 * 60 * 1000;  // <24h → cyan, else stone
        } catch { return false; }
      })();
      return {
        icon: <EnvelopeSimple size={11} weight="bold" />,
        text: rel ? `Email enviado · ${rel}` : "Email enviado",
        tone: recent ? "cyan" : "stone",
        title: ie.subject || "Email automático enviado",
      };
    }
    return {
      icon: <Warning size={11} weight="bold" />,
      text: "Email falló",
      tone: "red",
      title: ie.error || "Email automático falló",
    };
  })();

  const borderClass = critical
    ? "border-red-200"
    : signals.length > 0
      ? "border-amber-200"
      : "border-stone-200";

  const stop = (e) => e.stopPropagation();

  const moveTo = async (e, targetStatus) => {
    stop(e);
    if (!targetStatus || targetStatus === lead.status) return;
    if (needsConfirmMove(lead.status, targetStatus)) {
      if (!window.confirm(confirmMoveText(targetStatus, lead.status))) return;
    }
    await onPatch(lead, { status: targetStatus });
  };

  const prevStageKey = prevVisibleStatus(lead.status);
  const nextStageKey = nextVisibleStatus(lead.status);
  const visibleKey = visibleStep(lead.status);

  return (
    <div
      className={`group relative bg-white border rounded-md p-3 text-sm transition-all hover:border-stone-900 hover:shadow-sm ${borderClass} ${selected ? "ring-2 ring-emerald-500 border-emerald-500" : ""}`}
      data-testid={`lead-${lead.lead_id}`}
      data-focus-id={lead.lead_id}
    >
      {selectable && (
        <label
          className="absolute top-2 left-2 z-10 inline-flex items-center justify-center w-6 h-6 rounded bg-white/95 border border-stone-300 shadow-sm cursor-pointer hover:border-emerald-500"
          onClick={(e) => e.stopPropagation()}
          data-testid={`lead-select-${lead.lead_id}`}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect?.(lead)}
            aria-label={`Seleccionar ${lead.name || "lead"} para WhatsApp`}
            className="w-4 h-4 accent-emerald-600 cursor-pointer"
          />
        </label>
      )}
      {/* Click target — the whole card except controls */}
      <button
        type="button"
        onClick={() => onOpen?.(lead)}
        className={`w-full text-left ${selectable ? "pl-7" : ""}`}
        data-testid={`lead-open-${lead.lead_id}`}
      >
        {/* Header: status + signals + activity */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Chip
              tone={isColdLead(lead.status) ? "blue" : statusTone(visibleStep(lead.status))}
              size="xs"
              dotColor={LEAD_STATUS_MAP[visibleStep(lead.status)]?.dotColor || status?.dotColor}
              dot
            >
              {pipelineLabel(lead.status)}
            </Chip>
            {signals.length > 0 && (
              <span
                title={signals.map(s => SIGNAL_META[s]?.label || s).join(" · ")}
                data-testid={`lead-signal-${lead.lead_id}`}
                className={`shrink-0 w-1.5 h-1.5 rounded-full ${
                  critical ? "bg-red-500" : "bg-amber-500"
                }`}
              />
            )}
            {(lead.duplicate_submissions_count || 0) > 0 && (
              <span
                title={`Volvió a enviar el formulario ${lead.duplicate_submissions_count} ${lead.duplicate_submissions_count === 1 ? "vez" : "veces"}`}
                data-testid={`lead-dup-badge-${lead.lead_id}`}
                className="inline-flex items-center gap-0.5 shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800"
              >
                <ArrowsClockwise size={9} weight="bold" />×{lead.duplicate_submissions_count}
              </span>
            )}
          </div>
          {activity && (
            <span className="text-[10px] text-stone-400 shrink-0" title="Última actividad">
              {activity}
            </span>
          )}
        </div>

        {/* Owner badge — always rendered so unassigned leads are obvious. */}
        {(() => {
          const ownerName = lead.assigned_to_name
            || assignees.find(u => u.user_id === lead.assigned_to_user_id)?.name
            || null;
          const assigned = Boolean(lead.assigned_to_user_id);
          return (
            <div className="mb-1.5">
              <span
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${
                  assigned
                    ? "bg-stone-50 text-stone-700 border-stone-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
                data-testid={`lead-owner-badge-${lead.lead_id}`}
                title={assigned ? `Asignado a ${ownerName || "?"}` : "Lead sin responsable"}
              >
                <UserCircle size={10} weight="bold" />
                {assigned ? (ownerName || "Asignado") : "Sin asignar"}
              </span>
            </div>
          );
        })()}

        {/* Name + contact */}
        <div className="font-medium truncate group-hover:underline">{lead.name}</div>
        <div className="text-xs text-stone-500 truncate">
          {lead.email || lead.phone || <span className="italic text-stone-400">sin contacto</span>}
        </div>

        {/* Conversación — derivada de los marcadores del propio lead
            (`conversationsSelectors`), la misma regla que usa la bandeja de
            Conversaciones y el Centro de acción. Sin petición extra y sin
            copiar el estado del hilo al modelo del lead: el hilo sigue
            viviendo en su ficha. */}
        {hasConversation(lead) && (
          <div className="flex flex-wrap gap-1 mt-2">
            <Chip
              size="xs"
              tone={awaitingReply(lead) ? "red" : "emerald"}
              icon={<WhatsappLogo size={9} />}
              data-testid={`lead-conversation-${lead.lead_id}`}
              title={awaitingReply(lead)
                ? "El lead escribió lo último: está esperando respuesta"
                : "Hay conversación abierta con este lead"}
            >
              {awaitingReply(lead) ? "Esperando respuesta" : "Conversación"}
            </Chip>
          </div>
        )}

        {/* Tags row — interest + source + referral badge */}
        {(lead.interest || lead.source || lead.landing_source === "investor_referral") && (
          <div className="flex flex-wrap gap-1 mt-2">
            {lead.interest && <Chip size="xs" tone="stone" icon={<Tag size={9} />}>{lead.interest}</Chip>}
            {lead.source && lead.source !== "web" && (
              <Chip size="xs" tone="slate">{lead.source}</Chip>
            )}
            {lead.landing_source === "investor_referral" && (
              <Chip
                size="xs"
                tone="amber"
                data-testid={`lead-referral-badge-${lead.lead_id}`}
                title={lead.referred_by_name ? `Referido por ${lead.referred_by_name}` : "Referido de inversor"}
              >
                Referido
              </Chip>
            )}
          </div>
        )}

        {/* Context line — last note > last message. Single truncated line. */}
        {contextLine && (
          <div
            className="mt-2 text-[11px] text-stone-500 truncate italic"
            title={contextLine.text}
            data-testid={`lead-context-${lead.lead_id}`}
          >
            <span className="not-italic mr-1">{contextLine.kind === "note" ? "📝" : "💬"}</span>
            “{contextLine.text}”
            {(contextLine.who || contextLine.when) && (
              <span className="not-italic text-stone-400">
                {" · "}
                {[contextLine.who, contextLine.when].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        )}

        {/* Meta — próxima acción + responsable + reunión si activa */}
        <div className="mt-2">
          <MetaRow
            items={[
              meetingChip,
              meetingDoneSuggestion,
              initialEmailChip,
              lead.next_action
                ? {
                    icon: <ChatCircleDots size={11} />,
                    text: lead.next_action.length > 28 ? lead.next_action.slice(0, 28) + "…" : lead.next_action,
                    tone: due?.tone || "stone",
                    title: lead.next_action,
                  }
                : { icon: <ChatCircleDots size={11} />, text: "sin próxima acción", tone: "amber" },
              due ? { icon: <Calendar size={11} />, text: dueText, tone: due.tone } : null,
              assignee
                ? null /* avatar shown separately for visual hierarchy */
                : { icon: <UserCircle size={11} />, text: "sin responsable", tone: "amber" },
            ]}
          />
        </div>
      </button>

      {/* Assignee avatar bottom-right (only if assigned) */}
      {assignee && (
        <div className="absolute bottom-2 right-2">
          <Avatar id={assignee.user_id} name={assignee.name} size="xs" title={assignee.name} />
        </div>
      )}

      {/* Quick actions — revealed on hover/focus */}
      {expandedAction === null && (
        <div
          className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          onClick={stop}
        >
          <button
            type="button"
            onClick={(e) => moveTo(e, prevStageKey)}
            disabled={!prevStageKey}
            data-testid={`lead-prev-${lead.lead_id}`}
            title={prevStageKey ? `Etapa anterior: ${pipelineLabel(prevStageKey)}` : "Sin etapa anterior"}
            className="w-6 h-6 rounded bg-white border border-stone-200 hover:border-stone-900 hover:bg-stone-50 flex items-center justify-center text-stone-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={11} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => moveTo(e, nextStageKey)}
            disabled={!nextStageKey}
            data-testid={`lead-move-${lead.lead_id}`}
            title={nextStageKey ? `Mover a ${pipelineLabel(nextStageKey)}` : "Sin etapa siguiente"}
            className="w-6 h-6 rounded bg-white border border-stone-200 hover:border-stone-900 hover:bg-stone-50 flex items-center justify-center text-stone-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowRight size={11} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); setExpandedAction("stage"); }}
            data-testid={`lead-stage-menu-${lead.lead_id}`}
            title="Cambiar etapa"
            className="w-6 h-6 rounded bg-white border border-stone-200 hover:border-stone-900 hover:bg-stone-50 flex items-center justify-center text-stone-600"
          >
            <DotsThreeVertical size={12} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); setExpandedAction("assign"); }}
            data-testid={`lead-assign-${lead.lead_id}`}
            title="Asignar responsable"
            className="w-6 h-6 rounded bg-white border border-stone-200 hover:border-stone-900 hover:bg-stone-50 flex items-center justify-center text-stone-600"
          >
            <UserCircle size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); setExpandedAction("next"); }}
            data-testid={`lead-next-${lead.lead_id}`}
            title="Próxima acción"
            className="w-6 h-6 rounded bg-white border border-stone-200 hover:border-stone-900 hover:bg-stone-50 flex items-center justify-center text-stone-600"
          >
            <Calendar size={11} />
          </button>
        </div>
      )}

      {/* Inline editor: cambiar etapa */}
      {expandedAction === "stage" && (
        <InlineStagePicker
          currentVisibleKey={visibleKey}
          onCancel={() => setExpandedAction(null)}
          onPick={async (targetStatus) => {
            if (targetStatus === lead.status || targetStatus === visibleKey) {
              setExpandedAction(null); return;
            }
            if (needsConfirmMove(lead.status, targetStatus)) {
              if (!window.confirm(confirmMoveText(targetStatus, lead.status))) {
                return;
              }
            }
            await onPatch(lead, { status: targetStatus });
            setExpandedAction(null);
          }}
        />
      )}

      {/* Inline editor: Asignar responsable */}
      {expandedAction === "assign" && (
        <InlineAssign
          assignees={assignees}
          current={lead.assigned_to_user_id}
          onCancel={() => setExpandedAction(null)}
          onSave={async (userId) => {
            await onPatch(lead, { assigned_to_user_id: userId || null });
            setExpandedAction(null);
          }}
        />
      )}

      {/* Inline editor: Próxima acción + fecha */}
      {expandedAction === "next" && (
        <InlineNextAction
          current={{ next_action: lead.next_action, next_action_date: lead.next_action_date }}
          onCancel={() => setExpandedAction(null)}
          onSave={async (payload) => {
            await onPatch(lead, payload);
            setExpandedAction(null);
          }}
        />
      )}
    </div>
  );
}

/* --------------------- Inline editors (no popover lib) --------------------- */

function InlineStagePicker({ currentVisibleKey, onCancel, onPick }) {
  const cold = currentVisibleKey === COLD_LEAD_STATUS;
  return (
    <div
      className="absolute inset-x-2 top-2 z-10 bg-white border border-stone-300 rounded shadow-lg p-2"
      data-testid="lead-inline-stage"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">Cambiar etapa</div>
        <button
          type="button"
          onClick={onCancel}
          className="text-stone-400 hover:text-stone-700"
          aria-label="Cerrar"
        >
          <X size={11} />
        </button>
      </div>
      <ul className="max-h-64 overflow-auto -mx-1">
        {VISIBLE_PIPELINE_WITH_COLD.map((s) => {
          const isCurrent = s.key === currentVisibleKey;
          const isPast = !cold && s.step != null && currentVisibleKey !== COLD_LEAD_STATUS
            && (s.step < (VISIBLE_PIPELINE_WITH_COLD.find(x => x.key === currentVisibleKey)?.step ?? 0));
          const isCold = s.key === COLD_LEAD_STATUS;
          return (
            <li key={s.key}>
              <button
                type="button"
                disabled={isCurrent}
                onClick={() => onPick(s.key)}
                data-testid={`lead-inline-stage-${s.key}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition ${
                  isCurrent
                    ? "bg-stone-100 text-stone-900 cursor-default"
                    : "hover:bg-stone-50 text-stone-700"
                } ${isCold ? "border-t border-stone-100 mt-1 pt-2" : ""}`}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] shrink-0"
                  style={{ background: isPast ? s.dotColor : "transparent", border: `1px solid ${s.dotColor}` }}
                  aria-hidden="true"
                >
                  {isCold && <Snowflake size={8} weight="fill" />}
                </span>
                <span className="flex-1 truncate">{s.label}</span>
                {isCurrent && (
                  <span className="text-[9px] uppercase tracking-widest text-stone-400">Actual</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function InlineAssign({ assignees, current, onCancel, onSave }) {
  const [val, setVal] = useState(current || "");
  return (
    <div
      className="absolute inset-x-2 top-2 z-10 bg-white border border-stone-300 rounded shadow-lg p-2 space-y-2"
      data-testid="lead-inline-assign"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[10px] uppercase tracking-widest text-stone-500">Asignar responsable</div>
      <select
        autoFocus
        className="w-full text-xs border border-stone-200 rounded px-2 py-1"
        value={val}
        onChange={(e) => setVal(e.target.value)}
      >
        <option value="">— Sin asignar —</option>
        {assignees.map(u => (
          <option key={u.user_id} value={u.user_id}>{u.name}</option>
        ))}
      </select>
      <div className="flex gap-1 justify-end">
        <button onClick={onCancel} className="text-xs px-2 py-1 rounded text-stone-500 hover:text-stone-900"><X size={11} /></button>
        <button
          onClick={() => onSave(val)}
          data-testid="lead-inline-assign-save"
          className="text-xs px-2 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-1"
        >
          <Check size={11} weight="bold" /> Guardar
        </button>
      </div>
    </div>
  );
}

function InlineNextAction({ current, onCancel, onSave }) {
  const [text, setText] = useState(current.next_action || "");
  const [date, setDate] = useState((current.next_action_date || "").slice(0, 10));
  return (
    <div
      className="absolute inset-x-2 top-2 z-10 bg-white border border-stone-300 rounded shadow-lg p-2 space-y-2"
      data-testid="lead-inline-next"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[10px] uppercase tracking-widest text-stone-500">Próxima acción</div>
      <input
        autoFocus
        type="text"
        placeholder="Llamar / enviar dossier…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full text-xs border border-stone-200 rounded px-2 py-1"
      />
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="w-full text-xs border border-stone-200 rounded px-2 py-1"
      />
      <div className="flex gap-1 justify-end">
        <button onClick={onCancel} className="text-xs px-2 py-1 rounded text-stone-500 hover:text-stone-900"><X size={11} /></button>
        <button
          onClick={() => onSave({ next_action: text || null, next_action_date: date || null })}
          data-testid="lead-inline-next-save"
          className="text-xs px-2 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-1"
        >
          <Check size={11} weight="bold" /> Guardar
        </button>
      </div>
    </div>
  );
}
